import React, { useState } from 'react'
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    Typography,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { FormattedMessage, useIntl } from 'react-intl'

import LoginImage from '../../image/login_background.png'
import LogoImage from '../../image/logo_dark.png'
// Deliberately reuses the login screen's own layout and field styling rather than
// defining its own: this is the same first-run surface a seller sees, only with the
// organization step in place of the credentials step. Sharing these keeps the two
// screens visually identical instead of drifting apart.
import {
    Logo,
    Image,
    GridContainer,
    GridLeft,
    LogoContainer,
    FormContainer,
} from '../login-page/LoginPage.styled'
import { TextField, FormHelperText } from '../login-form/LoginForm.styled'
import loginPageMessages from '../login-page/LoginPage.messages'
import { formatVersionForDisplay } from '../../utils/version'
import { getOrganizationByName } from '../../config/organizations'
import messages from './OrganizationPicker.messages'

export interface IOwnProps {
    onSelect: (organizationId: string) => Promise<void>
}

export type IProps = IOwnProps

const OrganizationPicker: React.FC<IProps> = ({ onSelect }) => {
    const intl = useIntl()
    // A typed field, not a picker — the seller is told their organization's name
    // out of band and types it in, rather than choosing from a list in the app.
    const [organizationName, setOrganizationName] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState(false)

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOrganizationName(event.target.value)
        setError(false)
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        // onSelect is async (it awaits performLogoutCleanup before reloading);
        // disabling immediately on submit, not just while the field is empty,
        // stops a double tap from running cleanup twice.
        if (!organizationName || isSubmitting) {
            return
        }

        const organization = getOrganizationByName(organizationName)
        if (!organization) {
            setError(true)
            return
        }

        setIsSubmitting(true)
        try {
            await onSelect(organization.id)
            // On success onSelect reloads the page, so this component is about to
            // unmount — isSubmitting is deliberately left true rather than reset,
            // to avoid a flash of a re-enabled button right before the reload.
        } catch (submitError) {
            // Reset so a transient failure (e.g. AuthStorage.remove rejecting)
            // doesn't leave the button disabled forever with no way to retry.
            console.error('[OrganizationPicker] failed to select organization', submitError)
            setIsSubmitting(false)
        }
    }

    return (
        <GridContainer container>
            <GridLeft item xs={6}>
                <LogoContainer>
                    <Logo
                        src={LogoImage}
                        alt={intl.formatMessage({ ...loginPageMessages.logo })}
                    />
                </LogoContainer>

                <Box>
                    <Box mb={4}>
                        <Typography align='center' variant='h5'>
                            <FormattedMessage {...messages.title} />
                        </Typography>
                    </Box>

                    <FormContainer>
                        <form onSubmit={handleSubmit}>
                            <Box mb={4}>
                                <TextField
                                    placeholder={intl.formatMessage({ ...messages.label })}
                                    variant='outlined'
                                    autoCapitalize='none'
                                    autoCorrect='off'
                                    value={organizationName}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                    error={error}
                                    required
                                    autoFocus
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment
                                                position='start'
                                                disablePointerEvents
                                            >
                                                <BusinessIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Box>
                                <Button
                                    type='submit'
                                    color='primary'
                                    variant='contained'
                                    disabled={!organizationName || isSubmitting}
                                    fullWidth
                                >
                                    <FormattedMessage {...messages.continue} />
                                </Button>

                                {error ? (
                                    <FormHelperText error>
                                        <FormattedMessage {...messages.notFound} />
                                    </FormHelperText>
                                ) : null}
                            </Box>
                        </form>
                    </FormContainer>
                </Box>

                <Box pb={'15px'} textAlign='center' position='relative'>
                    <Typography
                        variant='caption'
                        color='textPrimary'
                        sx={{
                            position: 'absolute',
                            bottom: '15px',
                            right: '0',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            opacity: 0.9,
                        }}
                    >
                        {formatVersionForDisplay()}
                    </Typography>
                </Box>
            </GridLeft>

            <Grid item xs={6}>
                <Image
                    src={LoginImage}
                    alt={intl.formatMessage({ ...loginPageMessages.loginPage })}
                />
            </Grid>
        </GridContainer>
    )
}

export default OrganizationPicker
